import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Avatar,
  CardContent,
  Paper,
  CircularProgress,
  Fade,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LocalOffer as DiscountIcon,
  CleaningServices as CleaningIcon,
  Emergency as EmergencyIcon,
  FamilyRestroom as FamilyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  CreditCard as PaymentIcon,
  Info as InfoIcon,
  //HealthAndSafety as HealthIcon,
} from '@mui/icons-material';
import { getUser, createPaymentOrder } from '../services/api_service';
import { useAlert } from '../AlertContext';

// eslint-disable-next-line react/prop-types
const ModernPay = ({ pending, failed, success }) => {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [user, setUser] = useState({});
  const { showAlert } = useAlert();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const session = JSON.parse(localStorage.getItem('session') || '{}');

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const response = await getUser(session.user_id);
      if (response?.result) {
        setUser(response.result);
      }
    } catch (error) {
      showAlert('Error al cargar datos del usuario', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const response = await createPaymentOrder({ user_id: user.id });
      if (response?.init_point) {
        window.location.href = response.init_point;
      }
    } catch (error) {
      showAlert('Error al procesar el pago', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusConfig = () => {
    if (success) {
      return {
        icon: <CheckCircleIcon sx={{ fontSize: 64, color: '#4CAF50' }} />,
        title: 'PAGO APROBADO',
        message:
          'Hemos recibido tu pago con éxito. ¡Bienvenido a tu nuevo plan!',
        color: '#4CAF50',
      };
    }
    if (failed) {
      return {
        icon: <ErrorIcon sx={{ fontSize: 64, color: '#F44336' }} />,
        title: 'PAGO RECHAZADO',
        message:
          'No hemos podido procesar tu pago correctamente. Intenta nuevamente.',
        color: '#F44336',
      };
    }
    if (pending) {
      return {
        icon: <HourglassEmptyIcon sx={{ fontSize: 64, color: '#FF9800' }} />,
        title: 'PAGO PENDIENTE',
        message:
          'En este momento estamos procesando tu pago. Te notificaremos cuando esté listo.',
        color: '#FF9800',
      };
    }
    return null;
  };

  const benefits = [
    {
      icon: <DiscountIcon />,
      title: '20% descuento',
      subtitle: 'en tratamientos dentales',
      description: 'Aplicable a todos los procedimientos odontológicos',
      color: '#FF9800',
    },
    {
      icon: <CleaningIcon />,
      title: 'Higiene bucal profunda gratis',
      subtitle: 'cada 12 meses',
      description:
        'Limpieza dental profesional incluida en tu plan de afiliación',
      color: '#2196F3',
    },
    {
      icon: <EmergencyIcon />,
      title: 'Cita prioritaria',
      subtitle: 'en menos de 24 horas',
      description: 'Manejo prioritario en casos de dolor o sangrado',
      color: '#F44336',
    },
    {
      icon: <FamilyIcon />,
      title: '3 beneficiarios',
      subtitle: 'para tu familia',
      description: 'Comparte los beneficios con tu familia',
      color: '#4CAF50',
    },
  ];

  const statusConfig = getStatusConfig();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#94B754' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #F8FFFE 0%, #FFFFFF 100%)',
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="md">
        <Fade in={true} timeout={600}>
          <Box>
            {/* Header */}
            <img
              src="/white-logo.png"
              alt="Clodan Logo"
              style={{ height: 40 }}
            />
            {/*<Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background:
                    'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
                  mb: 2,
                }}
              >
                <HealthIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography
                variant={isMobile ? 'h4' : 'h3'}
                sx={{
                  fontWeight: 'bold',
                  color: '#2C5530',
                  mb: 1,
                }}
              >
                Plan de Afiliados
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Clodan - Clínica Dental
              </Typography>
            </Box>*/}

            {/* Main Card */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid #E0E0E0',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              }}
            >
              {statusConfig ? (
                /* Status Display */
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 6,
                    backgroundColor: `${statusConfig.color}10`,
                  }}
                >
                  {statusConfig.icon}
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 'bold',
                      color: statusConfig.color,
                      mt: 2,
                      mb: 2,
                    }}
                  >
                    {statusConfig.title}
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ maxWidth: 400, mx: 'auto' }}
                  >
                    {statusConfig.message}
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Price Section */}
                  <Box
                    sx={{
                      background:
                        'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
                      color: 'white',
                      p: 4,
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        borderRadius: 3,
                        py: 1,
                        px: 2,
                        display: 'inline-block',
                        mb: 2,
                        fontWeight: 600,
                      }}
                    >
                      💎 Plan de Afiliados
                    </Typography>
                    <Typography
                      variant={isMobile ? 'h3' : 'h2'}
                      sx={{ fontWeight: 'bold', mb: 1 }}
                    >
                      {formatCurrency(100000)}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      por año
                    </Typography>
                    {/*<Typography
                      variant="body2"
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        borderRadius: 2,
                        py: 1,
                        px: 2,
                        display: 'inline-block',
                        mt: 2,
                      }}
                    >
                      Ahorra más de $200.000 al año
                    </Typography>*/}
                  </Box>

                  {/* Benefits Section */}
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: '#2C5530',
                        mb: 3,
                        textAlign: 'center',
                      }}
                    >
                      ✨ Beneficios incluidos
                    </Typography>

                    <Grid container spacing={2}>
                      {benefits.map((benefit, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              p: 2,
                              borderRadius: 3,
                              border: `1px solid ${benefit.color}30`,
                              backgroundColor: `${benefit.color}08`,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 8px 24px ${benefit.color}20`,
                              },
                            }}
                          >
                            <Avatar
                              sx={{
                                backgroundColor: `${benefit.color}20`,
                                color: benefit.color,
                                mr: 2,
                                width: 48,
                                height: 48,
                              }}
                            >
                              {benefit.icon}
                            </Avatar>
                            <Box>
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 'bold', color: '#2C5530' }}
                              >
                                {benefit.title}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: benefit.color, fontWeight: 600 }}
                              >
                                {benefit.subtitle}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                              >
                                {benefit.description}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Info Box */}
                    <Box
                      sx={{
                        mt: 4,
                        p: 2,
                        backgroundColor: '#E3F2FD',
                        borderRadius: 3,
                        border: '1px solid #BBDEFB',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <InfoIcon sx={{ color: '#1976D2', mr: 2 }} />
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 'bold', color: '#1976D2' }}
                        >
                          Renovación inteligente
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Al renovar tu plan conservarás los meses restantes de
                          tu membresía actual
                        </Typography>
                      </Box>
                    </Box>

                    {/* Payment Button */}
                    <Box sx={{ mt: 4 }}>
                      <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        onClick={handlePayment}
                        disabled={processing}
                        startIcon={
                          processing ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <PaymentIcon />
                          )
                        }
                        sx={{
                          background:
                            'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
                          py: 2,
                          borderRadius: 3,
                          fontWeight: 'bold',
                          fontSize: '1.2rem',
                          textTransform: 'none',
                          boxShadow: '0 4px 12px rgba(148, 183, 84, 0.4)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 16px rgba(148, 183, 84, 0.5)',
                          },
                          '&:disabled': {
                            background: '#ccc',
                            transform: 'none',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        {processing ? 'Procesando...' : 'Proceder al Pago'}
                      </Button>
                    </Box>
                  </CardContent>
                </>
              )}
            </Paper>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default ModernPay;
